import BaseModal from 'components/BaseModal';
import React, { useState, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import { BASE_PADDING } from "constants/Layout";
import { Form, Col, Row, Typography, Button, Alert, Tabs} from "antd";
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import ProductAPI from 'restAPI/ProductAPI';
import SubProductAPI from 'restAPI/SubProductAPI';
import { CodeIconLarge } from 'components/Icons';
import { WidgetEmpty } from 'components/widgets/WidgetEmpty';
import ComingSoonTag from 'components/ComingSoonTag';
import Vocabulary, { getVocabulary }  from 'components/Vocabulary';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import { JENKINSSNIPPETFORSCRIPT, JENKINSSNIPPETFORPLUGIN, AZURESNIPPET, BAMBOOSNIPPET, DRONESNIPPET, CIRCLECISNIPPET, TEAMCITYSNIPPET, RELEASEIQSNIPPET, BUILDKITESNIPPET, GITHUBACTIONSSNIPPET, GITLABCISNIPPET } from './ToolsScript';
import { Environments } from 'views/Findings/Constants';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';
import _ from 'lodash';

const {Text, Link} = Typography;
const {TabPane} = Tabs;

const JenkinsFormPopup = forwardRef((props, ref) => {
    const [data, setData] = useState(() => props.initialValues || {});
    const [form] = Form.useForm();
    const thisRef = useRef();
    const [codeGenerated, setCodeGenerated] = useState();
    const toolId = props.id
    const selectedMenuKeyForJenkins = props.selectedMenuKeyForJenkins;
    const {productId, subProductId, environment, additionalAQLFilters} = data
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    useImperativeHandle(ref, () => thisRef.current);
    useUpdateEffect(() => {
        if(productId) {
            form.setFieldsValue({subProductId: null, environment: null})
        }
    },[productId, form])

    useUpdateEffect(() => {
        if(subProductId) {
            form.setFieldsValue({environment: null})
        }
    },[subProductId, form])

    const baseModalProps = {
        title: <>Configure</>,
        footer: null
    }

    const generateCode = async () => {
        setCodeGenerated(false)
        try {
            await form.validateFields();
            setCodeGenerated(true);
        } catch(err) {
            console.log(err)
        }
    }

    const subProductOptionsMutator = useCallback((options) => {
        const selectedValues = form.getFieldValue("subProductId") || [];
        const isAllSelected = selectedValues?.some(item => item.value === "All");
        const hasOtherSelected = selectedValues?.some(item => item.value !== "All");

        const allOption = { label: `All ${getVocabulary("subProduct", true)}`, value: "All" };
        const allOptions = !_.isEmpty(options) ? [allOption, ...options] : [];

        return allOptions.map(option => ({
            ...option,
            disabled: isAllSelected ? option.value !== "All" : hasOtherSelected && option.value === "All"
        }));
    }, [form]);

    const getCode = () => {
        let _envValue = environment;
        const mode = form.getFieldValue('mode');
        const productValue = selectedMenuKeyForJenkins === "jenkinsScript" && data.configType === "id" ? productId.value :  productId.label;

        // Handle subProductId array for Jenkins
        let subproductValue;
        if (toolId === "Jenkins" && Array.isArray(subProductId)) {
            // Check if "All" is selected
            const hasAll = subProductId.some(item => item.value === "All");
            if (hasAll) {
                subproductValue = '"subProducts": ["All"]';
            } else {
                // For Jenkins with multiple subproducts
                let subProductValues;
                if (selectedMenuKeyForJenkins === "jenkinsScript" && data.configType === "id") {
                    // Use IDs (values)
                    subProductValues = subProductId.map(item => String(item.value));
                } else {
                    subProductValues = subProductId.map(item => item.label);
                }
                subproductValue = `"subProducts": ${JSON.stringify(subProductValues)}`;
            }
        } else {
            // For non-Jenkins or single subproduct
            subproductValue = data.configType === "id" || subProductId?.value === "All" ? subProductId?.value : subProductId?.label;
        }
        if(hasDisabledEnvironment){
            _envValue = envValue;
        }

        let scriptKey = toolId;
        
        // If tool is Jenkins, determine script based on `selectedMenuKey`
        if (toolId === "Jenkins") {
            scriptKey = selectedMenuKeyForJenkins === "jenkinsPlugin" ? "JenkinsForPlugin" : "JenkinsForScript";
        }

        let scriptString = scripts[scriptKey].replace('{product}', productValue).replace('{subProduct}', subproductValue).replace('{envName}', _envValue).replace('{mode}', mode);

        if (_.isEmpty(additionalAQLFilters)) {
            scriptString = scriptString.replace(/,\s*"additionalAQLFilters":\s*\{additionalAQLFilters\}\s*/g, '');
        } else {
            scriptString = scriptString.replace("{additionalAQLFilters}", JSON.stringify(additionalAQLFilters).replace(/'/g, `'\\''`));
        }

        if((selectedMenuKeyForJenkins === "jenkinsPlugin") || (toolId === "Jenkins" && data.configType === 'name') || ['BuildKite', 'Drone', 'Circle_CI', 'GitHub_Actions', 'GitLab_CI', 'Azure_Pipeline'].includes(toolId)) {
            scriptString = scriptString.replace('buildvalidation', 'build')
        }

        return scriptString;
    };

    const codeTab = (
        <Tabs defaultActiveKey="1">
            <TabPane tab={requireYAMLScript.includes(toolId) ? "YAML" : "Bash"} key="1" style={{fontSize: '10px'}}>
                {codeGenerated ?
                <Typography.Text
                    copyable={{
                        text: getCode(),
                        tooltips: false,
                        icon: [<Button  style={{position: "absolute", top: "50px", right: "0"}} key="copy-icon" icon={<CopyOutlined />} type="link">Copy</Button>, <Button  style={{position: "absolute", top: "50px", right: "0"}} key="copied-icon" icon={<CheckOutlined />} type="link">Copied</Button>],
                    }}
                >
                    <pre>
                        {getCode()}
                    </pre>
                </Typography.Text>
                : <WidgetEmpty description={"Code will appear here once you click on generate button."} icon={<CodeIconLarge />}  />}
            </TabPane>
            <TabPane tab={<>Python <ComingSoonTag /></>} key="2">
                <WidgetEmpty description={"Code will appear here once you click on generate button."} icon={<CodeIconLarge />}  />
            </TabPane>
        </Tabs>
    )

    const fields = [
        {
            name: "productId",
            label: <Vocabulary name="product" />,
            type: FieldTypes.SELECT,
            colSpan: 12,
            showSearch: true,
            required: true,
            props: {
                api: ProductAPI.getAllQuick,
                labelInValue: true,
                autoFocus: true
            }
        },
        ...(toolId === "Jenkins" ? [  
            {
                name: "subProductId",
                label: <Vocabulary name="subProduct" />,
                type: FieldTypes.MULTI_SELECT,
                colSpan: 12,
                showSearch: true,
                required: true,
                props: {
                    labelInValue: true,
                    ...(productId?.value ? {
                        api: SubProductAPI.getAllQuick,
                        apiParams: productId?.value,
                        optionsMutator: subProductOptionsMutator,
                    } : {}),
                }
            }]: [
                    {
                        name: "subProductId",
                        label: <Vocabulary name="subProduct" />,
                        type: FieldTypes.SELECT,
                        colSpan: 12,
                        showSearch: true,
                        required: true,
                        props: {
                            labelInValue: true,
                            ...(productId?.value ? {
                                api: SubProductAPI.getAllQuick,
                                apiParams: productId?.value,
                            } : {})
                        }
                    }
        ]),
        ...(hasDisabledEnvironment ? [] : [{
            name: "environment",
            label: "Environment",
            type: FieldTypes.SELECT,
            colSpan: 12,
            showSearch: true,
            options: Environments,
            required: true,
        }]),
        ...(
            selectedMenuKeyForJenkins === "jenkinsScript"
            ? [
                {
                    name: "configType",
                    label: "Config Type",
                    type: FieldTypes.SELECT,
                    colSpan: 12,
                    showSearch: true,
                    options: [
                        { name: "ID", id: 'id' },
                        { name: "Name", id: 'name' }
                    ],
                    required: true,
                },
                {
                    name: "additionalAQLFilters",
                    label: "Additional AQL Filters",
                    colSpan: 24,
                    help: "Enter additional ArmorCode Query Language (AQL) filters to further refine the scope of findings used for release gate decisions. These filters will be applied in conjunction with the existing scope defined in the release management page."
                }
            ]
            : selectedMenuKeyForJenkins === "jenkinsPlugin"
            ? [
                {
                    name: "mode",
                    label: "Mode",
                    type: FieldTypes.SELECT,
                    colSpan: 12,
                    showSearch: true,
                    options: [
                        { name: "Warn", id: "warn" },
                        { name: "Block", id: "block" }
                    ],
                    required: true,
                }
            ]
            : []
        )
    ]

    return (
        <BaseModal
            modalProps={baseModalProps}
            ref={ref}
            afterClose={props.afterClose}
        >
            {selectedMenuKeyForJenkins === "jenkinsPlugin" ? (
                <>
                    <Text style={{ fontWeight: 'bold' }}>
                        Ensure the ArmorCode Jenkins Plugin is installed in your Jenkins instance. If not, please refer to the
                    </Text>
                    <Link
                        href="/api/jwt/zendesk/sso?return_to=https://support.armorcode.com/hc/en-us/articles/19601541733395-Jenkins#jenkins-plugin"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: 5 }}
                    >
                        Installation Documentation.
                    </Link>
                    <div style={{ marginTop: 16 }}>
                        Enter the details below to generate the ArmorCode release gate step, then insert into your Jenkins job.
                    </div>
                </>
            ) : (
                <Text>
                    Enter the details and click on generate button to generate the code.
                    Select desired scripting language from tab.
                </Text>
            )}

            <Form form={form} layout="vertical" onValuesChange={(_, values) => setData({...data, ...values})} initialValues={props.initialValues}>
                <Row className='p-t-l'>
                    <Col span={24}>
                        {generateFormFields(fields)}
                        <Form.Item className="text-right">
                            <Button type="primary" onClick={generateCode}>Generate</Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Row size={BASE_PADDING}>
                <Col span={24}>
                    <Alert description={codeTab} type="info" />
                </Col>
            </Row>
            {codeGenerated &&
                <Row className="m-y-sm">
                    <Col span={24}>
                        <Alert message={<Text>If you don't have an API token, generate it from <Link href="#/integrations/ArmorCode%20API">here</Link></Text>} type="success" />
                    </Col>
                </Row>
             }
        </BaseModal>
    )
})

const scripts = {
    JenkinsForScript: JENKINSSNIPPETFORSCRIPT,
    JenkinsForPlugin: JENKINSSNIPPETFORPLUGIN,
    Azure_Pipeline: AZURESNIPPET,
    Bamboo: BAMBOOSNIPPET,
    Drone: DRONESNIPPET,
    Circle_CI :CIRCLECISNIPPET,
    Team_City: TEAMCITYSNIPPET,
    ReleaseIQ: RELEASEIQSNIPPET,
    BuildKite: BUILDKITESNIPPET,
    GitHub_Actions: GITHUBACTIONSSNIPPET,
    GitLab_CI: GITLABCISNIPPET
}

const requireYAMLScript = ["Drone", "Circle_CI", "BuildKite", "GitHub_Actions", "GitLab_CI"]

export default JenkinsFormPopup;
